import { Injectable, Signal, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AvatarCollectionApiService } from './avatar-collection-api.service';
import { AvatarApiService } from './avatar-api.service';

export interface AvatarCollectionOption {
  id: number;
  name: string;
  isStylesAvailable: boolean;
  isSeasonalProposal: boolean;
}

@Injectable({ providedIn: 'root' })
export class AvatarCategoryTypesService {
  private readonly avatarCollectionApiService = inject(AvatarCollectionApiService);

  private readonly _collectionsForTemplate = signal<readonly AvatarCollectionOption[] | null>(null);
  readonly collectionsForTemplate: Signal<readonly AvatarCollectionOption[] | null> = this._collectionsForTemplate.asReadonly();
  private inflightCollectionsLoad: Promise<void> | null = null;

  async loadCollectionsForTemplate(): Promise<void> {
    if (this._collectionsForTemplate() !== null) {
      return;
    }
    if (this.inflightCollectionsLoad) {
      return this.inflightCollectionsLoad;
    }

    this.inflightCollectionsLoad = (async () => {
      try {
        const list = await firstValueFrom(this.avatarCollectionApiService.getAllCollections());
        this._collectionsForTemplate.set((list ?? []).map(c => ({
          id: c.id,
          name: c.collectionName,
          isStylesAvailable: c.isStylesAvailable,
          isSeasonalProposal: c.isSeasonalProposal
        })));
      } catch (error) {
        console.error('Failed to load collections for AI Avatar template:', error);
        this._collectionsForTemplate.set([]);
      } finally {
        this.inflightCollectionsLoad = null;
      }
    })();

    return this.inflightCollectionsLoad;
  }

  private readonly avatarApiService = inject(AvatarApiService);
  private readonly _categoryToTypes = signal<ReadonlyMap<number, ReadonlySet<number>> | null>(null);
  readonly categoryToTypes: Signal<ReadonlyMap<number, ReadonlySet<number>> | null> = this._categoryToTypes.asReadonly();

  private inflightLoad: Promise<void> | null = null;

  async load(): Promise<void> {
    if (this._categoryToTypes() !== null) {
      return;
    }
    if (this.inflightLoad) {
      return this.inflightLoad;
    }

    this.inflightLoad = (async () => {
      try {
        const raw = await this.avatarApiService.getCategoryTypesMap();
        const map = new Map<number, ReadonlySet<number>>();
        for (const [categoryId, typeIds] of Object.entries(raw ?? {})) {
          map.set(Number(categoryId), new Set(typeIds));
        }
        this._categoryToTypes.set(map);
      } catch (error) {
        console.error('Failed to load avatar category-types map:', error);
        this._categoryToTypes.set(new Map());
      } finally {
        this.inflightLoad = null;
      }
    })();

    return this.inflightLoad;
  }

  getTypesForCategory(categoryId: number | null | undefined): readonly number[] {
    if (categoryId === null || categoryId === undefined) {
      return [];
    }
    const map = this._categoryToTypes();
    if (!map) {
      return [];
    }
    const set = map.get(categoryId);
    return set ? Array.from(set) : [];
  }

  private readonly _avatarTypeLabels = signal<ReadonlyMap<number, string> | null>(null);
  readonly avatarTypeLabels: Signal<ReadonlyMap<number, string> | null> = this._avatarTypeLabels.asReadonly();
  private inflightLabelsLoad: Promise<void> | null = null;

  async loadAvatarTypeLabels(): Promise<void> {
    if (this._avatarTypeLabels() !== null) {
      return;
    }
    if (this.inflightLabelsLoad) {
      return this.inflightLabelsLoad;
    }

    this.inflightLabelsLoad = (async () => {
      try {
        const raw = await this.avatarApiService.getAvatarTypeLabels();
        const map = new Map<number, string>();
        for (const [typeId, label] of Object.entries(raw ?? {})) {
          map.set(Number(typeId), label);
        }
        this._avatarTypeLabels.set(map);
      } catch (error) {
        console.error('Failed to load avatar type labels:', error);
        this._avatarTypeLabels.set(new Map());
      } finally {
        this.inflightLabelsLoad = null;
      }
    })();

    return this.inflightLabelsLoad;
  }

  getAvatarTypeLabel(avatarTypeId: number | null | undefined): string {
    if (avatarTypeId === null || avatarTypeId === undefined) {
      return '';
    }
    return this._avatarTypeLabels()?.get(avatarTypeId) ?? '';
  }

  private readonly _defaultPromptsByType = signal<ReadonlyMap<number, string> | null>(null);
  private inflightDefaultPromptsLoad: Promise<void> | null = null;

  async getDefaultPromptForType(avatarTypeId: number): Promise<string> {
    await this.ensureDefaultPromptsLoaded();
    return this._defaultPromptsByType()?.get(avatarTypeId) ?? '';
  }

  private async ensureDefaultPromptsLoaded(): Promise<void> {
    if (this._defaultPromptsByType() !== null) {
      return;
    }
    if (this.inflightDefaultPromptsLoad) {
      return this.inflightDefaultPromptsLoad;
    }

    this.inflightDefaultPromptsLoad = (async () => {
      try {
        const groups = await this.avatarApiService.getDefaultPromptsGrouped();
        const map = new Map<number, string>();
        for (const group of groups ?? []) {
          for (const typeId of group.typeIds ?? []) {
            map.set(typeId, group.prompt);
          }
        }
        this._defaultPromptsByType.set(map);
      } catch (error) {
        console.error('Failed to load avatar default prompts:', error);
        this._defaultPromptsByType.set(new Map());
      } finally {
        this.inflightDefaultPromptsLoad = null;
      }
    })();

    return this.inflightDefaultPromptsLoad;
  }
}
